import React, { useMemo, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldOff, ArrowLeft, Check, Plus, Users, AlertTriangle, Star } from 'lucide-react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { fmtDate } from '@/lib/utils';
import CrmTaskFormModal from '@/components/admin/crm/CrmTaskFormModal';
import AdminSectionTabs from '@/components/admin/AdminSectionTabs';
import AdminCases from '@/pages/AdminCases';
import AdminCustomers from '@/pages/AdminCustomers';
import { CASE_TABS, satisfactionSummary } from '@/lib/caseQueue';

// Two views over one destination. The strip is link-based, so the customer
// directory keeps its own route and opens the CRM on its tab; the cases deep
// link opens the support desk, which is the default.
const CRM_TABS = [
  { to: '/admin/crm', label: 'SUPPORT & CASES', match: ['/admin/crm/cases'] },
  { to: '/admin/crm/directory', label: 'CUSTOMER DIRECTORY' },
];
import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';

export default function AdminCrm() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const tab = pathname.startsWith('/admin/crm/directory') ? 'directory' : 'support';
  // The status cards are the queue's filter, so the choice lives here where the
  // cards render. A ?tab= deep link opens the queue on that status.
  const [caseStatus, setCaseStatus] = useState(params.get('tab') || 'new');

  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ['crm-tasks-all'],
    queryFn: () => base44.entities.CustomerTask.list('due_date', 500),
    enabled: currentUser?.role === 'admin',
  });
  const { data: users = [] } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => base44.entities.User.list(),
    enabled: currentUser?.role === 'admin',
  });
  // Shared with the embedded queue below, so the satisfaction figure and the
  // working list always read the same fetch.
  const { data: cases = [] } = useQuery({
    queryKey: ['crm-cases'],
    queryFn: () => base44.entities.Case.list('-last_activity_at', 500),
    enabled: currentUser?.role === 'admin',
  });

  // Live counts for the status cards. Declared above the early returns so the
  // hook always runs in the same order.
  const caseCounts = useMemo(() => {
    const map = {};
    for (const t of CASE_TABS) map[t.id] = cases.filter(t.match).length;
    return map;
  }, [cases]);

  if (authLoading) {
    return <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
        </div>
      </div>
    );
  }

  const customers = users.filter((u) => u.role !== 'admin');
  const today = new Date(new Date().toDateString());
  const open = tasks.filter((t) => t.status === 'pending');
  const overdue = open.filter((t) => t.due_date && new Date(t.due_date) < today);
  const upcoming = open.filter((t) => t.due_date && new Date(t.due_date) >= today).sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

  // Satisfaction is the one CRM-wide figure that comes from the case set.
  const rating = satisfactionSummary(cases);

  const complete = async (t) => {
    try {
      await base44.entities.CustomerTask.update(t.id, { status: 'completed', completed_date: new Date().toISOString().slice(0, 10) });
      qc.invalidateQueries({ queryKey: ['crm-tasks-all'] });
    } catch (e) {
      toast({ title: 'Could not complete task', description: e.message, variant: 'destructive' });
    }
  };

  const SummaryItem = ({ icon: Icon, label, value, accent }) => (
    <div className="flex items-center gap-3">
      <Icon className={`w-4 h-4 shrink-0 ${accent}`} />
      <div>
        <p className="text-white font-black text-xl leading-none">{value}</p>
        <p className="text-white/30 text-[10px] font-bold tracking-widest mt-1">{label}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">ORDERS &amp; CUSTOMERS</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase">CUSTOMER CRM</h1>
          <p className="text-white/30 text-sm mt-1">Track customers, follow-ups and reminders in one place.</p>
        </div>
      </section>

      <div className="px-6 lg:px-10 max-w-6xl mx-auto">
        <AdminSectionTabs tabs={CRM_TABS} className="mt-6" />
      </div>

      {tab === 'support' && (
      <section className="px-6 lg:px-10 pt-8 max-w-6xl mx-auto space-y-6">
        {/* The status cards are the primary filter for the case list below. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {CASE_TABS.map((t) => {
            const active = caseStatus === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setCaseStatus(t.id)}
                aria-pressed={active}
                className={`text-left px-4 py-3 border transition-colors ${
                  active
                    ? 'border-primary bg-primary/10'
                    : 'border-white/5 bg-[#111115] hover:border-white/25'
                }`}
              >
                <p className={`font-black text-2xl leading-none ${active ? 'text-primary' : 'text-white'}`}>
                  {caseCounts[t.id] || 0}
                </p>
                <p className={`text-[10px] font-bold tracking-widest mt-1.5 ${active ? 'text-primary/80' : 'text-white/30'}`}>
                  {t.label.toUpperCase()}
                </p>
              </button>
            );
          })}
        </div>

        {/* CRM-wide figures — a compact secondary row under the filter. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 border border-white/5 bg-[#111115] px-5 py-3 mt-4">
          <SummaryItem icon={Users} label="TOTAL CUSTOMERS" value={customers.length} accent="text-primary" />
          <SummaryItem icon={AlertTriangle} label="OVERDUE FOLLOW-UPS" value={overdue.length} accent="text-red-400" />
          <SummaryItem icon={Star} label="AVG RATING" value={rating.total ? rating.average.toFixed(1) : '—'} accent="text-green-400" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-primary text-xs font-bold tracking-widest mb-1">TASKS &amp; REMINDERS</p>
              <h2 className="text-white font-black text-xl uppercase">FOLLOW-UPS</h2>
            </div>
            <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-wider px-4 py-2">
              <Plus className="w-3.5 h-3.5" /> NEW TASK
            </button>
          </div>
          {tasksLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
          ) : open.length === 0 ? (
            <p className="text-white/30 text-xs py-12 text-center border border-white/5 bg-[#111115]">No open tasks. You're all caught up.</p>
          ) : (
            <div className="space-y-2">
              {[...overdue, ...upcoming].map((t) => {
                const isOverdue = overdue.includes(t);
                return (
                  <div key={t.id} className="flex items-center gap-3 border border-white/5 bg-[#111115] px-4 py-3">
                    <button onClick={() => complete(t)} className="w-5 h-5 border border-white/20 hover:border-green-500/60 hover:text-green-400 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3" />
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-medium truncate">{t.title}</p>
                      <p className="text-white/40 text-xs truncate">{t.customer_name || t.customer_email}</p>
                    </div>
                    {t.priority === 'high' && <span className="text-[9px] font-bold tracking-wider text-red-400 shrink-0">HIGH</span>}
                    {t.due_date && (
                      <span className={`text-[10px] font-bold tracking-wider shrink-0 ${isOverdue ? 'text-red-400' : 'text-white/40'}`}>
                        {fmtDate(t.due_date)}{isOverdue && ' · OVERDUE'}
                      </span>
                    )}
                    <Link to="/admin/crm/directory" className="text-white/30 hover:text-primary text-[10px] font-bold tracking-wider shrink-0">VIEW</Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </section>
      )}

      {/* The case list itself — search sits directly over the results. */}
      {tab === 'support' && <AdminCases embedded status={caseStatus} />}
      {tab === 'directory' && <AdminCustomers embedded />}

      {showForm && (
        <CrmTaskFormModal
          email=""
          customerName=""
          customers={users}
          onClose={() => setShowForm(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['crm-tasks-all'] })}
        />
      )}
    </div>
  );
}